import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/app";
import { getMiniAppPlatform, isMiniAppContext } from "@/shared/lib/miniApp";
import { isMaxMiniApp, getMaxWebApp, getMaxInitData, readMaxInitDataFromLaunchUrl, } from "@/shared/lib/max";
import { isTelegramMiniApp, getTelegramWebApp } from "@/shared/lib/telegram";
import { initTelegramViewport, readTelegramViewportSnapshot, } from "@/shared/lib/telegramViewport";
/**
 * Диагностическая страница мини-аппа. Открывается по команде `/debug` в боте
 * MAX (кнопка-миниап с payload-маркером → App рендерит эту страницу вместо
 * обычного приложения — см. debugLaunch.ts). Задача — собрать ВСЁ, что нужно
 * для диагностики авторизации MAX (почему создаётся гость вместо аккаунта), и
 * дать заказчику скопировать одним нажатием.
 *
 * Безопасность: HMAC-подпись (hex 32+) маскируется; из user-JSON показываем
 * только ключи и id (без имени/PII). Остальное — структура, длины, флаги.
 */
/** Маскируем hex-подписи (подпись MAX = 64 hex) — единственное чувствительное. */
function maskSecrets(s) {
    if (s == null)
        return null;
    return s.replace(/[a-f0-9]{32,}/gi, (m) => `<hex:${m.length}>`);
}
/** Безопасное описание подписанной init_data: ключи/структура, без подписи/PII. */
function describeInitData(raw) {
    if (!raw)
        return { present: false };
    const info = { present: true, length: raw.length };
    try {
        const p = new URLSearchParams(raw);
        info.keys = [...p.keys()];
        const hash = p.get("hash");
        info.hash_len = hash?.length ?? null;
        info.hash_is_hex = hash ? /^[a-f0-9]+$/i.test(hash) : null;
        info.auth_date = p.get("auth_date");
        const user = p.get("user");
        if (user) {
            try {
                const j = JSON.parse(user);
                info.user_keys = Object.keys(j);
                info.user_id = j.id ?? null;
            }
            catch {
                info.user_json = "INVALID";
                info.user_len = user.length;
            }
        }
        else {
            info.user = null;
        }
    }
    catch (e) {
        info.parse_error = String(e);
    }
    return info;
}
/**
 * Во что РЕАЛЬНО разрешается CSS-выражение на этом клиенте. Нужно, чтобы
 * отличить «клиент не отдал инсеты» от «отдал, но переменная не доехала до
 * вёрстки»: пробник меряет ровно ту величину, которой пользуется раскладка.
 */
function measureCssLength(expression) {
    if (typeof document === "undefined" || !document.body)
        return null;
    const probe = document.createElement("div");
    probe.style.cssText = `position:absolute;top:0;left:0;width:0;visibility:hidden;pointer-events:none;height:${expression};`;
    document.body.appendChild(probe);
    const height = probe.getBoundingClientRect().height;
    probe.remove();
    return Math.round(height * 100) / 100;
}
function readCssVar(name) {
    if (typeof document === "undefined")
        return null;
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
    return value.length > 0 ? value : null;
}
/**
 * Полный срез fullscreen/safe-area: и то, что сказал клиент, и то, что легло в
 * CSS. `measure: false` — для ленивого инициализатора useState: пробник трогает
 * DOM, а делать это в фазе рендера нельзя (в StrictMode инициализатор ещё и
 * вызывается дважды). Первый честный замер снимает эффект.
 */
function collectViewportDebug(measure = true) {
    return {
        ...readTelegramViewportSnapshot(),
        css_vars: {
            "--tgvp-top": readCssVar("--tgvp-top"),
            "--tgvp-right": readCssVar("--tgvp-right"),
            "--tgvp-bottom": readCssVar("--tgvp-bottom"),
            "--tgvp-left": readCssVar("--tgvp-left"),
        },
        resolved_px: {
            "--miniapp-safe-t": measure
                ? measureCssLength("var(--miniapp-safe-t)")
                : null,
            "--miniapp-safe-b": measure
                ? measureCssLength("var(--miniapp-safe-b)")
                : null,
            "env(safe-area-inset-top)": measure
                ? measureCssLength("env(safe-area-inset-top, 0px)")
                : null,
            "env(safe-area-inset-bottom)": measure
                ? measureCssLength("env(safe-area-inset-bottom, 0px)")
                : null,
        },
        window: {
            innerHeight: typeof window !== "undefined" ? window.innerHeight : null,
            screenHeight: typeof window !== "undefined" ? window.screen?.height ?? null : null,
        },
    };
}
/** Разбор сырого query/hash: под каким ключом что лежит (ключ к диагностике MAX). */
function analyzeLaunchParams(qs) {
    const out = [];
    if (!qs)
        return out;
    try {
        for (const [k, v] of new URLSearchParams(qs)) {
            const entry = { key: k, value_len: v.length };
            const looksPacked = /hash=/i.test(v) ||
                /auth_date/i.test(v) ||
                /%3D/i.test(v) ||
                /user/i.test(v);
            if (looksPacked) {
                entry.parsed = describeInitData(v);
                try {
                    const dec = decodeURIComponent(v);
                    if (dec !== v)
                        entry.parsed_decoded_once = describeInitData(dec);
                }
                catch {
                    /* ignore */
                }
            }
            else {
                entry.value = v.length > 100 ? `${v.slice(0, 100)}…` : v;
            }
            out.push(entry);
        }
    }
    catch (e) {
        out.push({ parse_error: String(e) });
    }
    return out;
}
function safeKeys(obj) {
    if (!obj || typeof obj !== "object")
        return null;
    try {
        return Object.keys(obj);
    }
    catch {
        return null;
    }
}
export const DebugPage = () => {
    const auth = useAuth();
    const [copied, setCopied] = useState(false);
    const taRef = useRef(null);
    // Инсеты и фуллскрин приезжают асинхронно (события клиента), поэтому срез
    // пересобираем по таймеру — так на телефоне видно и сам переход в фуллскрин.
    // initTelegramViewport() здесь refcount-нут: подписка уже поднята в index.tsx,
    // повторный вызов её не задваивает.
    const [tgViewport, setTgViewport] = useState(() => collectViewportDebug(false));
    useEffect(() => {
        const release = initTelegramViewport();
        setTgViewport(collectViewportDebug());
        // Раз в секунду, а не чаще: каждый тик пересобирает весь отчёт и заново
        // гоняет JSON.stringify по многокилобайтной структуре — на слабом Android
        // это заметный джанк ровно там, где человек читает диагностику.
        const timer = window.setInterval(() => setTgViewport(collectViewportDebug()), 1000);
        return () => {
            window.clearInterval(timer);
            release();
        };
    }, []);
    const report = useMemo(() => {
        const w = typeof window !== "undefined" ? window : {};
        const snap = w.__LAUNCH_DEBUG ?? null;
        const maxApp = getMaxWebApp();
        const tgApp = getTelegramWebApp();
        const searchForAnalysis = snap?.search ?? w.location?.search ?? "";
        const hashForAnalysis = (snap?.hash ?? w.location?.hash ?? "").replace(/^#/, "");
        return {
            collected_at: new Date().toISOString(),
            verdict: {
                platform: getMiniAppPlatform(),
                isMaxMiniApp: isMaxMiniApp(),
                isTelegramMiniApp: isTelegramMiniApp(),
                isMiniAppContext: isMiniAppContext(),
                isAuthenticated: auth.isAuthenticated,
                isGuest: auth.isGuest,
                isLoading: auth.isLoading,
            },
            launch_snapshot: snap
                ? {
                    age_ms: typeof snap.ts === "number" ? Date.now() - snap.ts : null,
                    href: maskSecrets(snap.href),
                    search: maskSecrets(snap.search),
                    hash: maskSecrets(snap.hash),
                    referrer: snap.referrer,
                }
                : "MISSING (window.__LAUNCH_DEBUG нет — старый index.html?)",
            current_url: {
                href: maskSecrets(w.location?.href),
                search: maskSecrets(w.location?.search),
                hash: maskSecrets(w.location?.hash),
            },
            launch_url_analysis: {
                query_params: analyzeLaunchParams(searchForAnalysis.replace(/^\?/, "")),
                hash_params: analyzeLaunchParams(hashForAnalysis),
            },
            max_bridge: {
                present: !!maxApp,
                settled_flag: w.__maxBridgeSettled ?? null,
                initData: describeInitData(getMaxWebApp()?.initData),
                initDataUnsafe_keys: safeKeys(getMaxWebApp()?.initDataUnsafe),
                start_param_unsafe: getMaxWebApp()?.initDataUnsafe
                    ?.start_param ?? null,
                top_keys: safeKeys(maxApp),
                platform_field: maxApp?.platform ?? null,
            },
            telegram_bridge: {
                present: !!tgApp,
                settled_flag: w.__tgBridgeSettled ?? null,
                initData: describeInitData(getTelegramWebApp()?.initData),
            },
            tg_viewport: tgViewport,
            max_initdata_resolution: {
                getMaxInitData: describeInitData(getMaxInitData()),
                readMaxInitDataFromLaunchUrl: describeInitData(readMaxInitDataFromLaunchUrl()),
            },
            auth_user: (() => {
                const u = auth.user;
                if (!u)
                    return null;
                return {
                    id: u.id ?? null,
                    telegram_id: u.telegram_id ?? null,
                    max_id: u.max_id ?? u.max_user_id ?? null,
                    is_guest: u.is_guest ?? null,
                    registration_method: u.registration_method ?? null,
                };
            })(),
            env: {
                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                language: typeof navigator !== "undefined" ? navigator.language : null,
                build: process.env.REACT_APP_BUILD ??
                    process.env.NODE_ENV ??
                    null,
            },
        };
        // Пересобираем при каждом рендере кнопки — auth может дозагрузиться.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        auth.isAuthenticated,
        auth.isGuest,
        auth.isLoading,
        auth.user,
        tgViewport,
    ]);
    const json = useMemo(() => JSON.stringify(report, null, 2), [report]);
    // Активная проверка: шлём initData прямо на /users/me и показываем, что
    // ОТВЕТИЛ бэкенд. Отличает «фронт не нашёл initData» от «бэкенд отверг
    // initData» (HMAC/просрочка/юзер не найден) — AuthProvider на провале MAX
    // молча чистит данные, поэтому статус сервера иначе не виден.
    const [probe, setProbe] = useState("(не запускалось — нажмите кнопку)");
    const [probing, setProbing] = useState(false);
    const runProbe = async () => {
        setProbing(true);
        setProbe("запрос…");
        try {
            // Срезаем хвостовой слэш: REACT_APP_API_URL бывает с "/" на конце, иначе
            // получаем "https://api.virtuoz.pro//api/..." (axios чистит сам, fetch — нет).
            const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
            const initData = getMaxInitData() ?? "";
            const res = await fetch(`${base}/api/v1/public/users/me`, {
                method: "GET",
                headers: { "X-Max-Init-Data": initData },
                credentials: "include",
            });
            const text = await res.text();
            if (res.ok) {
                let brief = text;
                try {
                    const j = JSON.parse(text);
                    brief =
                        `id=${String(j.id)}, is_guest=${String(j.is_guest ?? "(нет поля)")}` +
                            `, username=${String(j.username ?? null)}`;
                }
                catch {
                    /* оставляем как есть */
                }
                setProbe(`HTTP ${res.status} OK — ${brief}`);
            }
            else {
                setProbe(`HTTP ${res.status}\n${(maskSecrets(text) ?? "").slice(0, 800)}`);
            }
        }
        catch (e) {
            setProbe(`network error: ${String(e)}`);
        }
        finally {
            setProbing(false);
        }
    };
    const fullText = useMemo(() => `${json}\n\n=== AUTH PROBE (/users/me) ===\n${probe}`, [json, probe]);
    const handleCopy = async () => {
        let ok = false;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(fullText);
                ok = true;
            }
        }
        catch {
            /* fallthrough к execCommand */
        }
        if (!ok && taRef.current) {
            taRef.current.focus();
            taRef.current.select();
            try {
                ok = document.execCommand("copy");
            }
            catch {
                ok = false;
            }
        }
        setCopied(ok);
        window.setTimeout(() => setCopied(false), 2500);
    };
    const v = report.verdict;
    return (_jsxs("div", { style: {
            minHeight: "100vh",
            background: "#101012",
            color: "#e8e8ea",
            // Страница минует шелл мини-аппа, поэтому отступ под хром клиента берём
            // здесь напрямую — заодно это визуальная проверка, что он вообще есть.
            padding: "calc(var(--miniapp-safe-t) + 16px) 16px calc(var(--miniapp-safe-b) + 16px)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: 12,
            lineHeight: 1.5,
            boxSizing: "border-box",
        }, children: [_jsx("h1", { style: { fontSize: 16, margin: "0 0 12px" }, children: "\u0414\u0438\u0430\u0433\u043D\u043E\u0441\u0442\u0438\u043A\u0430 \u043C\u0438\u043D\u0438-\u0430\u043F\u043F\u0430" }), _jsxs("div", { style: {
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "2px 12px",
                    padding: 12,
                    background: "#17171a",
                    borderRadius: 8,
                    marginBottom: 12,
                }, children: [_jsx("span", { children: "platform" }), _jsx("b", { style: { color: v.platform ? "#4ade80" : "#f87171" }, children: String(v.platform) }), _jsx("span", { children: "isMaxMiniApp" }), _jsx("b", { style: { color: v.isMaxMiniApp ? "#4ade80" : "#f87171" }, children: String(v.isMaxMiniApp) }), _jsx("span", { children: "isAuthenticated" }), _jsx("b", { style: { color: v.isAuthenticated ? "#4ade80" : "#f87171" }, children: String(v.isAuthenticated) }), _jsx("span", { children: "isGuest" }), _jsx("b", { style: { color: v.isGuest ? "#fbbf24" : "#9ca3af" }, children: String(v.isGuest) }), _jsx("span", { children: "isLoading" }), _jsx("b", { style: { color: v.isLoading ? "#fbbf24" : "#9ca3af" }, children: String(v.isLoading) })] }), _jsxs("div", { style: {
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "2px 12px",
                    padding: 12,
                    background: "#17171a",
                    borderRadius: 8,
                    marginBottom: 12,
                }, children: [_jsx("span", { style: { gridColumn: "1 / -1", color: "#9ca3af" }, children: "\u2014 Telegram viewport \u2014" }), _jsx("span", { children: "platform / device" }), _jsxs("b", { children: [String(tgViewport.platform), " \u2192 ", tgViewport.deviceClass] }), _jsx("span", { children: "version" }), _jsx("b", { children: String(tgViewport.version) }), _jsx("span", { children: "isFullscreen" }), _jsx("b", { style: { color: tgViewport.isFullscreen ? "#4ade80" : "#fbbf24" }, children: String(tgViewport.isFullscreen) }), _jsx("span", { children: "swipes enabled" }), _jsx("b", { style: {
                            color: tgViewport.isVerticalSwipesEnabled ? "#fbbf24" : "#4ade80",
                        }, children: String(tgViewport.isVerticalSwipesEnabled) }), _jsx("span", { children: "safeArea.top / content.top" }), _jsxs("b", { children: [String(tgViewport.rawSafeAreaInset?.top ?? "—"), " /", " ", String(tgViewport.rawContentSafeAreaInset?.top ?? "—")] }), _jsx("span", { children: "--tgvp-top" }), _jsx("b", { children: String(tgViewport.css_vars["--tgvp-top"]) }), _jsx("span", { children: "--miniapp-safe-t (px)" }), _jsx("b", { style: {
                            color: (tgViewport.resolved_px["--miniapp-safe-t"] ?? 0) > 0
                                ? "#4ade80"
                                : "#f87171",
                        }, children: String(tgViewport.resolved_px["--miniapp-safe-t"]) }), _jsx("span", { children: "--miniapp-safe-b (px)" }), _jsx("b", { children: String(tgViewport.resolved_px["--miniapp-safe-b"]) })] }), v.isLoading && (_jsx("p", { style: { color: "#fbbf24", margin: "0 0 12px" }, children: "\u23F3 \u0410\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u044F \u0435\u0449\u0451 \u0438\u0434\u0451\u0442 \u2014 \u043F\u043E\u0434\u043E\u0436\u0434\u0438\u0442\u0435 \u043F\u0430\u0440\u0443 \u0441\u0435\u043A\u0443\u043D\u0434, \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u0432\u044B\u0448\u0435 \u043E\u0431\u043D\u043E\u0432\u044F\u0442\u0441\u044F \u0441\u0430\u043C\u0438. \u041A\u043E\u043F\u0438\u0440\u0443\u0439\u0442\u0435 \u043F\u043E\u0441\u043B\u0435 \u0442\u043E\u0433\u043E, \u043A\u0430\u043A isLoading \u0441\u0442\u0430\u043D\u0435\u0442 false." })), _jsx("button", { onClick: runProbe, disabled: probing, style: {
                    width: "100%",
                    padding: "14px",
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#fff",
                    background: probing ? "#3a3a42" : "#2563eb",
                    border: "none",
                    borderRadius: 10,
                    cursor: probing ? "default" : "pointer",
                    marginBottom: 8,
                }, children: probing ? "Проверяю…" : "1) Проверить вход на сервере" }), _jsx("pre", { style: {
                    margin: "0 0 12px",
                    padding: 12,
                    background: "#0b0b0d",
                    border: "1px solid #2a2a30",
                    borderRadius: 8,
                    color: probe.startsWith("HTTP 200") ? "#4ade80" : "#e8e8ea",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }, children: probe }), _jsx("button", { onClick: handleCopy, style: {
                    width: "100%",
                    padding: "14px",
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#101012",
                    background: copied ? "#4ade80" : "#8b5cf6",
                    border: "none",
                    borderRadius: 10,
                    cursor: "pointer",
                    marginBottom: 12,
                }, children: copied ? "✓ Скопировано" : "2) Копировать всё" }), _jsx("p", { style: { color: "#9ca3af", margin: "0 0 8px" }, children: "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u00AB\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C \u0432\u0445\u043E\u0434\u00BB, \u0437\u0430\u0442\u0435\u043C \u00AB\u041A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u0432\u0441\u0451\u00BB \u0438 \u043F\u0440\u0438\u0448\u043B\u0438\u0442\u0435 \u0442\u0435\u043A\u0441\u0442 \u0440\u0430\u0437\u0440\u0430\u0431\u043E\u0442\u0447\u0438\u043A\u0443. \u0415\u0441\u043B\u0438 \u043A\u043D\u043E\u043F\u043A\u0430 \u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F \u043D\u0435 \u0441\u0440\u0430\u0431\u043E\u0442\u0430\u043B\u0430 \u2014 \u0432\u044B\u0434\u0435\u043B\u0438\u0442\u0435 \u0442\u0435\u043A\u0441\u0442 \u043D\u0438\u0436\u0435 \u0432\u0440\u0443\u0447\u043D\u0443\u044E. \u041F\u043E\u0434\u043F\u0438\u0441\u044C \u0438 \u043B\u0438\u0447\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435 \u0441\u043A\u0440\u044B\u0442\u044B." }), _jsx("textarea", { ref: taRef, readOnly: true, value: fullText, onFocus: (e) => e.currentTarget.select(), style: {
                    width: "100%",
                    minHeight: "50vh",
                    background: "#0b0b0d",
                    color: "#c8c8cc",
                    border: "1px solid #2a2a30",
                    borderRadius: 8,
                    padding: 12,
                    fontFamily: "inherit",
                    fontSize: 11,
                    whiteSpace: "pre",
                    boxSizing: "border-box",
                } })] }));
};
export default DebugPage;
